import type { ApiError } from './types'

const BASE = '/api'

export class ApiClientError extends Error implements ApiError {
  status: number
  detail?: unknown
  constructor(status: number, message: string, detail?: unknown) {
    super(message)
    this.status = status
    this.detail = detail
  }
}

function makeError(status: number, body: unknown): ApiClientError {
  let message = `Request failed (${status})`
  if (body && typeof body === 'object' && 'detail' in body) {
    const detail = (body as { detail: unknown }).detail
    if (typeof detail === 'string') message = detail
  }
  return new ApiClientError(status, message, body)
}

async function parseBody(res: Response): Promise<unknown> {
  const contentType = res.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    try {
      return await res.json()
    } catch {
      return null
    }
  }
  const text = await res.text()
  return text || null
}

interface RequestOptions {
  signal?: AbortSignal
  headers?: Record<string, string>
}

export async function apiGet<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'GET',
    credentials: 'include',
    signal: options.signal,
    headers: {
      Accept: 'application/json',
      ...(options.headers || {}),
    },
  })
  const body = await parseBody(res)
  if (!res.ok) throw makeError(res.status, body)
  return body as T
}

export async function apiJson<T>(
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  options: RequestOptions & { idempotencyKey?: string } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  }
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    signal: options.signal,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (res.status === 204) return undefined as T
  const parsed = await parseBody(res)
  if (!res.ok) throw makeError(res.status, parsed)
  return parsed as T
}

export function apiPost<T>(path: string, body?: unknown, idempotencyKey?: string, signal?: AbortSignal): Promise<T> {
  return apiJson<T>('POST', path, body, { idempotencyKey, signal })
}

export async function apiPostMultipart<T>(
  path: string,
  form: FormData,
  idempotencyKey?: string,
  signal?: AbortSignal,
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    signal,
    headers,
    body: form,
  })
  if (res.status === 204) return undefined as T
  const parsed = await parseBody(res)
  if (!res.ok) throw makeError(res.status, parsed)
  return parsed as T
}

export function apiPatch<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return apiJson<T>('PATCH', path, body, { signal })
}

export function apiDelete<T = void>(path: string, signal?: AbortSignal): Promise<T> {
  return apiJson<T>('DELETE', path, undefined, { signal })
}

export function isApiError(error: unknown): error is ApiClientError {
  return error instanceof ApiClientError
}
